import './TrackmanParameterCategories.scss';

import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';

import Paragraph from 'components/TextComps/Paragraph';
import { AdminPagesRelativeRoutes } from 'Routes';
import { TrackmanParameterCategoryDto, TrackmanParameterSubcategoryDto } from 'types/admin/trackmanParameterCategories';
import useSetPageTitle from 'utilities/useSetPageTitle';
import { useParametersFiltersStore } from 'stores/Admin/Editors/ParametersFiltersStore';
import { useTrackmanParameterCategories } from 'queryHooks/Admin/TrackmanParameterCategories/useTrackmanParameterCategories';
import { useCreateTrackmanParameterCategory } from 'queryHooks/Admin/TrackmanParameterCategories/useCreateTrackmanParameterCategory';
import { useUpdateTrackmanParameterCategory } from 'queryHooks/Admin/TrackmanParameterCategories/useUpdateTrackmanParameterCategory';
import { useDeleteTrackmanParameterCategory } from 'queryHooks/Admin/TrackmanParameterCategories/useDeleteTrackmanParameterCategory';
import { useReorderTrackmanParameterCategories } from 'queryHooks/Admin/TrackmanParameterCategories/useReorderTrackmanParameterCategories';

import { ReactComponent as PencilIcon } from 'images/icons/Pencil.svg';
import { ReactComponent as TrashIcon } from 'images/icons/Trash.svg';
import { ReactComponent as PlusIcon } from 'images/icons/Plus.svg';
import { ReactComponent as ArrowUpIcon } from 'images/icons/ArrowUp.svg';
import { ReactComponent as ArrowDownIcon } from 'images/icons/ArrowDown.svg';
import { ReactComponent as ChevronLeftIcon } from 'images/icons/ChevronLeftThin.svg';

import { getCategoryUsageCount, isDuplicateSubcategoryTitle, moveArrayItemById, toSubcategoryRequests } from './trackmanParameterCategoriesHelpers';
import { TitleFormModal } from './TitleFormModal';
import { DeleteCategoryNodeModal } from './DeleteCategoryNodeModal';
import { CategoryTranslationProgress } from './CategoryTranslationProgress';
import { TranslationStatusChip } from 'pages/Admin/ModulesV2/TranslationStatusChip/TranslationStatusChip';

type CategoryModalState = { mode: 'create' } | { mode: 'edit'; category: TrackmanParameterCategoryDto } | null;
type SubcategoryModalState = { mode: 'create' } | { mode: 'edit'; subcategory: TrackmanParameterSubcategoryDto } | null;

export const TrackmanParameterCategories = () => {
  useSetPageTitle({ title: 'Trackman Parameter Categories' });
  const navigate = useNavigate();
  const setFilterByCategory = useParametersFiltersStore((state) => state.setFilterByCategory);

  const { data: categories = [], isPending } = useTrackmanParameterCategories();
  const create = useCreateTrackmanParameterCategory();
  const update = useUpdateTrackmanParameterCategory();
  const deleteCategory = useDeleteTrackmanParameterCategory();
  const reorder = useReorderTrackmanParameterCategories();

  const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(null);

  const [categoryModal, setCategoryModal] = useState<CategoryModalState>(null);
  const [subcategoryModal, setSubcategoryModal] = useState<SubcategoryModalState>(null);
  const [subcategoryModalError, setSubcategoryModalError] = useState<string | undefined>(undefined);
  const [categoryToDelete, setCategoryToDelete] = useState<TrackmanParameterCategoryDto | null>(null);
  const [subcategoryToDelete, setSubcategoryToDelete] = useState<TrackmanParameterSubcategoryDto | null>(null);

  useEffect(() => {
    const stillVisible = categories.some((category) => category.id === selectedCategoryId);
    if (!stillVisible) {
      setSelectedCategoryId(categories[0]?.id ?? null);
    }
  }, [categories, selectedCategoryId]);

  const selectedCategory = useMemo(
    () => categories.find((category) => category.id === selectedCategoryId) ?? null,
    [categories, selectedCategoryId]
  );

  const subcategories = selectedCategory?.subcategories ?? [];

  const saveCategory = (categoryId: string, title: string, subcategories: TrackmanParameterSubcategoryDto[]) => {
    update.mutate({
      categoryId,
      request: { id: categoryId, title, subcategories: toSubcategoryRequests(subcategories) },
    });
  };

  const handleReorderCategory = (categoryId: string, direction: 'up' | 'down') => {
    const reordered = moveArrayItemById(categories, categoryId, direction);
    reorder.mutate(reordered.map((category) => category.id));
  };

  const handleReorderSubcategory = (categoryId: string, subcategoryId: string, direction: 'up' | 'down') => {
    if (!selectedCategory) return;
    const reordered = moveArrayItemById(subcategories, subcategoryId, direction);
    saveCategory(categoryId, selectedCategory.title, reordered);
  };

  const handleSaveCategory = (title: string) => {
    if (categoryModal?.mode === 'edit') {
      saveCategory(categoryModal.category.id, title, categoryModal.category.subcategories);
    } else {
      const id = uuidv4();
      create.mutate(
        { id, title, subcategories: [] },
        { onSuccess: () => setSelectedCategoryId(id) }
      );
    }
    setCategoryModal(null);
  };

  const handleSaveSubcategory = (title: string) => {
    if (!selectedCategory) return;

    const ignoreId = subcategoryModal?.mode === 'edit' ? subcategoryModal.subcategory.id : undefined;
    if (isDuplicateSubcategoryTitle(selectedCategory, title, ignoreId)) {
      setSubcategoryModalError(`A subcategory named "${title}" already exists in ${selectedCategory.title}.`);
      return;
    }

    if (subcategoryModal?.mode === 'edit') {
      const subcategoryId = subcategoryModal.subcategory.id;
      const updated = subcategories.map((subcategory) =>
        subcategory.id === subcategoryId ? { ...subcategory, title } : subcategory
      );
      saveCategory(selectedCategory.id, selectedCategory.title, updated);
    } else {
      const newSubcategory: TrackmanParameterSubcategoryDto = { id: uuidv4(), title, usageCount: 0 };
      saveCategory(selectedCategory.id, selectedCategory.title, [...subcategories, newSubcategory]);
    }

    setSubcategoryModalError(undefined);
    setSubcategoryModal(null);
  };

  const handleConfirmDeleteCategory = () => {
    if (!categoryToDelete) return;
    deleteCategory.mutate(categoryToDelete.id);
    setCategoryToDelete(null);
  };

  const handleConfirmDeleteSubcategory = () => {
    if (!selectedCategory || !subcategoryToDelete) return;
    const remaining = subcategories.filter((subcategory) => subcategory.id !== subcategoryToDelete.id);
    saveCategory(selectedCategory.id, selectedCategory.title, remaining);
    setSubcategoryToDelete(null);
  };

  const goToFilteredParameters = (categoryTitle: string) => {
    setFilterByCategory(categoryTitle);
    setCategoryToDelete(null);
    setSubcategoryToDelete(null);
    navigate(`/Admin/${AdminPagesRelativeRoutes.TrackmanParametersListV2}`);
  };

  return (
    <>
      <div className='default-page-header'>
        <h1 className='page-title'>Trackman Parameter Categories</h1>
      </div>
      <div className='admin-content-container trackman-parameter-categories'>
        <div className='categories-toolbar'>
          <div className='categories-toolbar-actions'>
            <Link to={`/Admin/${AdminPagesRelativeRoutes.TrackmanParametersListV2}`} className='styled-button tertiary back-link'>
              <ChevronLeftIcon />
              <span>Back to parameters</span>
            </Link>
            <button className='styled-button link-button primary' onClick={() => setCategoryModal({ mode: 'create' })}>
              <PlusIcon />
              <span>New category</span>
            </button>
          </div>
        </div>

        <div className='categories-columns'>
          <div className='categories-column'>
            <div className='categories-column-header'>
              <h2>Categories</h2>
            </div>
            <div className='categories-column-body'>
              {isPending ? (
                <Paragraph className='no-results'>
                  <>Loading categories…</>
                </Paragraph>
              ) : categories.length === 0 ? (
                <div className='empty-state-card'>
                  <div>
                    <Paragraph>
                      <>No categories yet.</>
                    </Paragraph>
                  </div>
                  <button className='styled-button primary' onClick={() => setCategoryModal({ mode: 'create' })}>
                    Create category
                  </button>
                </div>
              ) : (
                categories.map((category, index) => (
                  <div
                    key={category.id}
                    className={`category-row ${category.id === selectedCategoryId ? 'active' : ''}`}
                    onClick={() => setSelectedCategoryId(category.id)}
                  >
                    <span className='category-row-title'>
                      <span className='category-row-title-text'>{category.title}</span>
                      <TranslationStatusChip status={category.translationProgress?.status} />
                    </span>
                    <div className='action-buttons'>
                      <button
                        className={`row-icon-button ${index === 0 ? 'disabled' : ''}`}
                        disabled={index === 0}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleReorderCategory(category.id, 'up');
                        }}
                      >
                        <ArrowUpIcon />
                      </button>
                      <button
                        className={`row-icon-button ${index === categories.length - 1 ? 'disabled' : ''}`}
                        disabled={index === categories.length - 1}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleReorderCategory(category.id, 'down');
                        }}
                      >
                        <ArrowDownIcon />
                      </button>
                      <button
                        className='row-button'
                        onClick={(e) => {
                          e.stopPropagation();
                          setCategoryModal({ mode: 'edit', category });
                        }}
                      >
                        <PencilIcon />
                      </button>
                      <button
                        className='row-button'
                        onClick={(e) => {
                          e.stopPropagation();
                          setCategoryToDelete(category);
                        }}
                      >
                        <TrashIcon />
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          <div className='categories-column'>
            <div className='categories-column-header'>
              <h2>Subcategories{selectedCategory ? ` in ${selectedCategory.title}` : ''}</h2>
              <button
                className='styled-button link-button tertiary'
                disabled={!selectedCategory}
                onClick={() => {
                  setSubcategoryModalError(undefined);
                  setSubcategoryModal({ mode: 'create' });
                }}
              >
                <PlusIcon />
                <span>New subcategory</span>
              </button>
            </div>
            <div className='categories-column-body'>
              {!selectedCategory ? (
                <Paragraph className='no-results'>
                  <>Select a category to view its subcategories.</>
                </Paragraph>
              ) : subcategories.length === 0 ? (
                <div className='empty-state-card'>
                  <div>
                    <Paragraph>
                      <>No subcategories yet.</>
                    </Paragraph>
                  </div>
                  <button
                    className='styled-button primary'
                    onClick={() => {
                      setSubcategoryModalError(undefined);
                      setSubcategoryModal({ mode: 'create' });
                    }}
                  >
                    Create subcategory
                  </button>
                </div>
              ) : (
                subcategories.map((subcategory, index) => (
                  <div key={subcategory.id} className='category-row'>
                    <span className='category-row-title'>{subcategory.title}</span>
                    <div className='action-buttons'>
                      <button
                        className={`row-icon-button ${index === 0 ? 'disabled' : ''}`}
                        disabled={index === 0}
                        onClick={() => handleReorderSubcategory(selectedCategory.id, subcategory.id, 'up')}
                      >
                        <ArrowUpIcon />
                      </button>
                      <button
                        className={`row-icon-button ${index === subcategories.length - 1 ? 'disabled' : ''}`}
                        disabled={index === subcategories.length - 1}
                        onClick={() => handleReorderSubcategory(selectedCategory.id, subcategory.id, 'down')}
                      >
                        <ArrowDownIcon />
                      </button>
                      <button
                        className='row-button'
                        onClick={() => {
                          setSubcategoryModalError(undefined);
                          setSubcategoryModal({ mode: 'edit', subcategory });
                        }}
                      >
                        <PencilIcon />
                      </button>
                      <button className='row-button' onClick={() => setSubcategoryToDelete(subcategory)}>
                        <TrashIcon />
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        {selectedCategory && <CategoryTranslationProgress category={selectedCategory} />}
      </div>

      <TitleFormModal
        open={categoryModal !== null}
        title={categoryModal?.mode === 'edit' ? 'Edit category' : 'New category'}
        initialValue={categoryModal?.mode === 'edit' ? categoryModal.category.title : ''}
        onCancel={() => setCategoryModal(null)}
        onSave={handleSaveCategory}
      />

      <TitleFormModal
        open={subcategoryModal !== null}
        title={subcategoryModal?.mode === 'edit' ? 'Edit subcategory' : 'New subcategory'}
        contextLabel={selectedCategory ? `Parent category: ${selectedCategory.title}` : undefined}
        initialValue={subcategoryModal?.mode === 'edit' ? subcategoryModal.subcategory.title : ''}
        errorMessage={subcategoryModalError}
        onCancel={() => {
          setSubcategoryModalError(undefined);
          setSubcategoryModal(null);
        }}
        onSave={handleSaveSubcategory}
      />

      <DeleteCategoryNodeModal
        open={categoryToDelete !== null}
        itemName='category'
        nodeLabel={categoryToDelete?.title ?? ''}
        usageCount={categoryToDelete ? getCategoryUsageCount(categoryToDelete) : 0}
        onCancel={() => setCategoryToDelete(null)}
        onConfirmDelete={handleConfirmDeleteCategory}
        onViewAffectedParameters={() => categoryToDelete && goToFilteredParameters(categoryToDelete.title)}
      />

      <DeleteCategoryNodeModal
        open={subcategoryToDelete !== null}
        itemName='subcategory'
        nodeLabel={subcategoryToDelete?.title ?? ''}
        usageCount={subcategoryToDelete?.usageCount ?? 0}
        onCancel={() => setSubcategoryToDelete(null)}
        onConfirmDelete={handleConfirmDeleteSubcategory}
        onViewAffectedParameters={() => selectedCategory && goToFilteredParameters(selectedCategory.title)}
      />
    </>
  );
};

export default TrackmanParameterCategories;
